import {
  Action,
  Actions,
  ActionSequence,
  wrapAction,
} from 'src/lib/ActionSequence';
import { State } from 'src/state';
import StateObserver, { user } from 'src/StateObserver';
import { defaultLaunchSequence } from './DefaultLaunchSequence';
import { adminLaunchSequence } from './AdminMessagesLaunchSequence';
import { waitForAssets } from 'src/lib/utils';
import { petSpeciesAssets } from 'src/loadingGroups';
import * as Pet from 'src/replicant/pet';
import { appendToConversationData } from '../conversation/conversation.utils';
import { Feature } from 'src/replicant/analytics';
import { puzzleCoinsLaunchSequence } from './PuzzleCoinsLaunchSequence';
import { puzzleCoinsIsPlayingTutorial } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { guestStateMigrationSequence } from './GuestStateMigrationSequence';

/**
 * Creates launch sequence from an array of actions. The actions are executed in order.
 * @param actions
 * @returns
 */
export function createLaunchSequence(actions: Action[]): Actions {
  return actions;
}

/**
 * Creates a new launch sequence action block. Returning true from an action here, will only skip the block.
 * @param block
 * @returns
 */
export function actionBlock({
  actions,
  skip,
  enabled,
}: {
  actions: Actions;
  skip?: (state: State, now: number) => boolean;
  enabled?: (state: State, now: number) => boolean;
}): Action {
  return async () => {
    if (skip?.(StateObserver.getState(), StateObserver.now())) {
      return false;
    }

    // Reverse to skip
    if (enabled && !enabled?.(StateObserver.getState(), StateObserver.now())) {
      return false;
    }

    for (const action of actions) {
      if (await wrapAction(action).execute()) {
        break;
      }
    }

    return false;
  };
}

/**
 * Creates a single action. Returning true from the action here will actually stop the sequence.
 * @returns
 */
export function singleAction({
  action,
  skip,
  enabled,
}: {
  action: Action;
  skip?: (state: State, now: number) => boolean;
  enabled?: (state: State, now: number) => boolean;
}): Action {
  return async () => {
    if (skip?.(StateObserver.getState(), StateObserver.now())) {
      return false;
    }

    // Reverse to skip
    if (enabled && !enabled?.(StateObserver.getState(), StateObserver.now())) {
      return false;
    }

    // Run the action - this can stop the sequence if return true
    return await wrapAction(action).execute();
  };
}

/**
 * Starts the active launch sequence.
 * @returns
 */
export async function startLaunchSequence(): Promise<void> {
  let launchSequence = defaultLaunchSequence;
  if (puzzleCoinsIsPlayingTutorial(user())) {
    launchSequence = puzzleCoinsLaunchSequence;
  }

  await ActionSequence.start(
    [...adminLaunchSequence, ...guestStateMigrationSequence, ...launchSequence],
    'Launch',
  );

  // Wait for pet assets to load.
  const species = Pet.getters.getEquippedPetSpecies(
    StateObserver.getState().user,
  );

  if (!species) {
    return;
  }

  await waitForAssets(petSpeciesAssets[species], {
    feature: Feature.PET._,
    subFeature: Feature.PET.ASSETS,
  });
  appendToConversationData(species);
}
